<template>
	<v-dialog :value="value" @input="$emit('input', $event)" max-width="750px" scrollable>
		<v-card>
			<v-card-title class="d-flex align-center pa-4">
				<v-icon class="mr-2">mdi-history</v-icon>
				<div>
					<span style="font-size: 1.6rem; font-weight: 500">Audit History</span>
					<span v-if="entityLabel" class="entity-label ml-2">{{ entityLabel }}</span>
					<span v-if="totalCount > 0" class="event-count ml-2">{{ totalCount }} events</span>
				</div>
				<v-spacer />
				<v-btn icon @click="$emit('input', false)">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-card-title>

			<!-- Search -->
			<div class="px-4 pb-2">
				<v-text-field
					v-model="searchQuery"
					prepend-inner-icon="mdi-magnify"
					placeholder="Filter events..."
					dense
					outlined
					hide-details
					clearable
					class="search-input"
				/>
			</div>

			<v-divider />

			<v-card-text class="pa-0 timeline-scroll" ref="timelineScroll">
				<div v-if="loading && items.length === 0" class="d-flex justify-center pa-8">
					<v-progress-circular indeterminate color="primary" />
				</div>

				<div v-else-if="items.length === 0" class="text-center pa-8 grey--text">
					No activity recorded yet.
				</div>

				<div v-else-if="filteredItems.length === 0" class="text-center pa-8 grey--text">
					No events match "{{ searchQuery }}"
				</div>

				<template v-else>
					<!-- Load earlier history -->
					<div v-if="hasEarlierHistory" class="d-flex justify-center pa-2">
						<v-btn text small color="primary" :loading="loadingEarlier" @click="loadEarlier">
							<v-icon small class="mr-1">mdi-history</v-icon>
							Load Earlier History (showing {{ items.length }} of {{ totalCount }})
						</v-btn>
					</div>

					<!-- Quote Phase -->
					<div v-if="filteredQuotePhaseItems.length > 0" class="phase-section">
						<div class="phase-header phase-header-clickable" @click="quotePhaseExpanded = !quotePhaseExpanded">
							<v-icon small class="mr-1">{{ quotePhaseExpanded ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
							<v-icon small class="mr-1">mdi-file-document-outline</v-icon>
							Quote Phase
							<span class="phase-count ml-2">({{ filteredQuotePhaseItems.length }})</span>
						</div>
						<v-timeline v-if="quotePhaseExpanded" dense class="activity-timeline">
							<template v-for="group in groupedQuoteItems">
								<v-timeline-item
									v-for="(item, idx) in group.items"
									:key="item.activityLogId"
									:color="dotColor(item.outcome, item.requestName)"
									:icon="actionIcon(item.requestName)"
									icon-color="white"
									fill-dot
								>
									<div class="timeline-entry">
										<div v-if="idx === 0" class="timeline-meta">
											<span class="timestamp">{{ formatDateLocal(item.timestamp) }}</span>
											<span class="relative-time">{{ relativeTime(item.timestamp) }}</span>
											<user-badge v-if="item.userName" :name="item.userName" />
										</div>
										<div class="timeline-description" v-html="displayText(item)"></div>
										<div v-if="item.failureMessage" class="timeline-error">
											{{ item.failureMessage }}
										</div>
									</div>
								</v-timeline-item>
							</template>
						</v-timeline>
					</div>

					<!-- Order Phase -->
					<div v-if="filteredOrderPhaseItems.length > 0" class="phase-section">
						<div class="phase-header phase-header-clickable" @click="orderPhaseExpanded = !orderPhaseExpanded">
							<v-icon small class="mr-1">{{ orderPhaseExpanded ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
							<v-icon small class="mr-1">mdi-cart-outline</v-icon>
							Order Phase
							<span class="phase-count ml-2">({{ filteredOrderPhaseItems.length }})</span>
						</div>
						<v-timeline v-if="orderPhaseExpanded" dense class="activity-timeline">
							<template v-for="group in groupedOrderItems">
								<v-timeline-item
									v-for="(item, idx) in group.items"
									:key="item.activityLogId"
									:color="dotColor(item.outcome, item.requestName)"
									:icon="actionIcon(item.requestName)"
									icon-color="white"
									fill-dot
								>
									<div class="timeline-entry">
										<div v-if="idx === 0" class="timeline-meta">
											<span class="timestamp">{{ formatDateLocal(item.timestamp) }}</span>
											<span class="relative-time">{{ relativeTime(item.timestamp) }}</span>
											<user-badge v-if="item.userName" :name="item.userName" />
										</div>
										<div class="timeline-description" v-html="displayText(item)"></div>
										<div v-if="item.failureMessage" class="timeline-error">
											{{ item.failureMessage }}
										</div>
									</div>
								</v-timeline-item>
							</template>
						</v-timeline>
					</div>

				</template>
			</v-card-text>
		</v-card>
	</v-dialog>
</template>

<script>
import DateFormatting from "@/mixins/DateFormatting";
import { GET_ACTIVITY_LOGS } from "@/gql/queries";
import { getActivityLogSignalR } from "@/services/ActivityLogSignalRService";

// Inline user badge component (uses render function — runtime-only Vue build has no template compiler)
const UserBadge = {
	name: "UserBadge",
	props: { name: String },
	computed: {
		initials() {
			if (!this.name) return "?";
			const parts = this.name.trim().split(/\s+/);
			if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
			return this.name.substring(0, 2).toUpperCase();
		},
		color() {
			let hash = 0;
			for (let i = 0; i < this.name.length; i++) {
				hash = this.name.charCodeAt(i) + ((hash << 5) - hash);
			}
			const colors = ["#1565c0", "#2e7d32", "#7b1fa2", "#c62828", "#e65100", "#00838f", "#4527a0", "#5d4037", "#f9a825"];
			return colors[Math.abs(hash) % colors.length];
		},
	},
	render(h) {
		return h("span", {
			class: "user-badge",
			style: { backgroundColor: this.color },
			attrs: { title: this.name },
		}, this.initials);
	},
};

const ACTION_ICONS = {
	"Created Quote": "mdi-file-document-edit-outline",
	"Updated Quote": "mdi-file-document-edit-outline",
	"Added Item": "mdi-package-variant",
	"Added Sub-Item": "mdi-package-variant",
	"Assigned Item": "mdi-package-variant-closed-check",
	"Added Draft Item": "mdi-file-document-edit-outline",
	"Removed Item": "mdi-package-variant-remove",
	"Colors Selected": "mdi-palette",
	"Colors Updated": "mdi-palette",
	"Sizes Set": "mdi-resize",
	"Sizes Updated": "mdi-resize",
	"Special Sizes Set": "mdi-resize",
	"Special Sizes Updated": "mdi-resize",
	"Line Item Note": "mdi-note-text-outline",
	"Line Item Note Updated": "mdi-note-text-outline",
	"Line Item Note Removed": "mdi-note-remove-outline",
	"Updated Notes": "mdi-note-text-outline",
	"S&H Updated": "mdi-truck-outline",
	"S&H Discount Updated": "mdi-tag-outline",
	"Tax Rate Updated": "mdi-percent",
	"Contact Changed": "mdi-account-switch-outline",
	"Bill To Changed": "mdi-map-marker-outline",
	"Ship To Changed": "mdi-map-marker-outline",
	"Sales Rep Changed": "mdi-account-tie-outline",
	"Shipping Method Changed": "mdi-truck-fast-outline",
	"Payment Terms Changed": "mdi-file-sign",
	"Expected Order Date Changed": "mdi-calendar-edit",
	"School PO Changed": "mdi-school-outline",
	"Reference # Changed": "mdi-pound",
	"Attribution Changed": "mdi-tag-text-outline",
	"Updated Payments": "mdi-credit-card-outline",
	"Payment Added": "mdi-credit-card-plus-outline",
	"Payment Updated": "mdi-credit-card-outline",
	"Payment Removed": "mdi-credit-card-remove-outline",
	"Placed Order": "mdi-cart-check",
	"Processed Order": "mdi-cog",
	"Line Item Status Changed": "mdi-swap-horizontal",
	"Inventory Committed": "mdi-warehouse",
	"Pick Ticket Created": "mdi-ticket-outline",
	"Pick Ticket Unshipped": "mdi-ticket-outline",
	"Sub-Item Added to Processing Pick Ticket": "mdi-plus-box-outline",
	"Parent Added to Processing Pick Ticket": "mdi-plus-box-outline",
	"Processing Pick Ticket Converted to Normal": "mdi-check-decagram",
	"Sub-Item Removed from Processing Pick Ticket": "mdi-minus-box-outline",
	"Line Item Unshipped": "mdi-package-variant-remove",
	"Purchase Order Generated": "mdi-truck-delivery-outline",
	"Dropship PO Generated": "mdi-truck-delivery",
	"Inventory Received": "mdi-package-down",
	"Inventory Unreceived": "mdi-package-up",
	"Received Inventory Committed": "mdi-check-decagram",
	"Received Inventory Decommitted": "mdi-close-circle-outline",
	"Submitted for Approval": "mdi-send",
	"Approved Quote": "mdi-check-circle-outline",
	"Cancelled Quote": "mdi-cancel",
	"Restored Quote": "mdi-restore",
	"Reverted to Quote": "mdi-undo",
	"Quantity Changed": "mdi-counter",
	"Text Options Set": "mdi-form-textbox",
	"Text Options Updated": "mdi-form-textbox",
	"Lettering Set": "mdi-format-letter-case",
	"Lettering Added": "mdi-format-letter-case",
	"Lettering Updated": "mdi-format-letter-case",
	"Lettering Removed": "mdi-format-letter-case-lower",
	"Pricing Set": "mdi-currency-usd",
	"Pricing Changed": "mdi-currency-usd",
	"Drop Ship": "mdi-truck-outline",
	"Drop Ship Changed": "mdi-truck-outline",
	"Braid Position Set": "mdi-vector-line",
	"Braid Position Changed": "mdi-vector-line",
	"Braid Position Removed": "mdi-vector-line",
	"Match Previous PO Set": "mdi-link-variant",
	"Match Previous PO Changed": "mdi-link-variant",
	"Match Previous PO Removed": "mdi-link-variant",
	"Colors Cleared": "mdi-palette-outline",
	"Sizes Cleared": "mdi-resize",
	"Special Sizes Cleared": "mdi-resize",
	SubmitForApproval: "mdi-send",
	ApproveQuote: "mdi-check-circle-outline",
	CancelQuote: "mdi-cancel",
	RestoreQuote: "mdi-restore",
	RevertToPending: "mdi-undo",
	"Cancelled Order": "mdi-cancel",
	"Restored Order": "mdi-restore",
	CancelOrder: "mdi-cancel",
	RestoreOrder: "mdi-restore",
	UpdateOrder: "mdi-file-document-edit-outline",
	"Shipping Label Created": "mdi-label-outline",
	"Shipping Label Voided": "mdi-label-off-outline",
	"Document Printed": "mdi-printer",
	"Auto-Print Failed": "mdi-printer-alert",
	PrintDocument: "mdi-printer-outline",
	UpdatePurchaseOrder: "mdi-pencil-outline",
	CreatePurchaseOrder: "mdi-truck-delivery-outline",
	CancelPurchaseOrder: "mdi-cancel",
	RestorePurchaseOrder: "mdi-restore",
	LockPurchaseOrder: "mdi-lock-outline",
	EditPurchaseOrderVendor: "mdi-store-edit-outline",
	EditPurchaseOrderDate: "mdi-calendar-edit",
	AddOrUpdatePoNotes: "mdi-note-text-outline",
};

const ORDER_PHASE_ACTIONS = new Set([
	"Placed Order",
	"Processed Order",
	"UpdateOrder",
	"CancelOrder",
	"RestoreOrder",
	"Line Item Status Changed",
	"Inventory Committed",
	"Pick Ticket Created",
	"Pick Ticket Unshipped",
	"Sub-Item Added to Processing Pick Ticket",
	"Parent Added to Processing Pick Ticket",
	"Processing Pick Ticket Converted to Normal",
	"Sub-Item Removed from Processing Pick Ticket",
	"Purchase Order Generated",
	"Dropship PO Generated",
	"Inventory Received",
	"Inventory Unreceived",
	"Received Inventory Committed",
	"Received Inventory Decommitted",
	"Cancelled Order",
	"Restored Order",
	"Document Printed",
	"Auto-Print Failed",
	"Shipping Label Created",
	"Shipping Label Voided",
	"UpdatePurchaseOrder",
	"CreatePurchaseOrder",
	"CancelPurchaseOrder",
	"RestorePurchaseOrder",
	"LockPurchaseOrder",
	"EditPurchaseOrderVendor",
	"EditPurchaseOrderDate",
	"AddOrUpdatePoNotes",
]);

export default {
	name: "ActivityHistoryDialog",
	mixins: [DateFormatting],
	components: { UserBadge },
	props: {
		value: { type: Boolean, default: false },
		quoteId: { type: Number, default: null },
		orderId: { type: Number, default: null },
		quoteNumber: { type: String, default: null },
		orderNumber: { type: String, default: null },
	},
	data() {
		return {
			quotePhaseExpanded: !this.orderId,
			orderPhaseExpanded: true,
			items: [],
			totalCount: 0,
			loading: false,
			pageSize: 100,
			searchQuery: "",
			signalRUnsubscribe: null,
			loadingEarlier: false,
		};
	},
	beforeDestroy() {
		this.unsubscribeSignalR();
	},
	computed: {
		entityLabel() {
			if (this.orderNumber) return `Order ${this.orderNumber}`;
			if (this.quoteNumber) return `Quote ${this.quoteNumber}`;
			if (this.orderId) return `Order #${this.orderId}`;
			if (this.quoteId) return `Quote #${this.quoteId}`;
			return "";
		},

		filteredItems() {
			if (!this.searchQuery) return this.items;
			const q = this.searchQuery.toLowerCase();
			return this.items.filter((item) => {
				const text = (item.requestSummary || "") + " " + (item.requestName || "") + " " + (item.userName || "");
				return text.toLowerCase().includes(q);
			});
		},

		filteredQuotePhaseItems() {
			return this.filteredItems.filter((item) => !this.isOrderPhase(item));
		},
		filteredOrderPhaseItems() {
			return this.filteredItems.filter((item) => this.isOrderPhase(item));
		},

		groupedQuoteItems() {
			return this.groupByTimestamp(this.filteredQuotePhaseItems);
		},
		groupedOrderItems() {
			return this.groupByTimestamp(this.filteredOrderPhaseItems);
		},
		hasEarlierHistory() {
			return this.items.length < this.totalCount;
		},
	},
	watch: {
		value(open) {
			if (open) {
				this.items = [];
				this.totalCount = 0;
				this.searchQuery = "";
				this.quotePhaseExpanded = !this.orderId;
				this.orderPhaseExpanded = true;
				this.fetchLogs();
				this.subscribeSignalR();
			} else {
				this.unsubscribeSignalR();
			}
		},
	},
	methods: {
		async fetchLogs() {
			if (!this.quoteId && !this.orderId) return;

			this.loading = true;
			try {
				const where = this.buildWhereFilter();

				// First, get totalCount with a minimal query
				const countRes = await this.$apollo.query({
					query: GET_ACTIVITY_LOGS,
					variables: { where, order: [{ timestamp: "ASC" }], skip: 0, take: 1 },
					fetchPolicy: "no-cache",
				});

				this.totalCount = countRes.data.activityLogs.totalCount;

				// Load the latest page (skip earlier entries)
				const skip = Math.max(0, this.totalCount - this.pageSize);
				const res = await this.$apollo.query({
					query: GET_ACTIVITY_LOGS,
					variables: {
						where,
						order: [{ timestamp: "ASC" }],
						skip,
						take: this.pageSize,
					},
					fetchPolicy: "no-cache",
				});

				this.items = res.data.activityLogs.items;

				// Auto-scroll to bottom after loading
				this.$nextTick(() => {
					const el = this.$refs.timelineScroll;
					if (el) el.scrollTop = el.scrollHeight;
				});
			} catch (err) {
				console.error("Failed to load activity logs", err);
			} finally {
				this.loading = false;
			}
		},

		async loadEarlier() {
			this.loadingEarlier = true;
			try {
				const where = this.buildWhereFilter();

				// Calculate how many earlier entries exist
				const earlierCount = this.totalCount - this.items.length;
				const take = Math.min(this.pageSize, earlierCount);
				const skip = Math.max(0, earlierCount - take);

				const res = await this.$apollo.query({
					query: GET_ACTIVITY_LOGS,
					variables: {
						where,
						order: [{ timestamp: "ASC" }],
						skip,
						take,
					},
					fetchPolicy: "no-cache",
				});

				// Prepend older entries
				this.items = [...res.data.activityLogs.items, ...this.items];
			} catch (err) {
				console.error("Failed to load earlier logs", err);
			} finally {
				this.loadingEarlier = false;
			}
		},

		buildWhereFilter() {
			const orFilters = [];
			if (this.quoteId) orFilters.push({ quoteId: { eq: this.quoteId } });
			if (this.orderId) orFilters.push({ orderId: { eq: this.orderId } });
			return orFilters.length > 1 ? { or: orFilters } : orFilters[0];
		},

		subscribeSignalR() {
			this.unsubscribeSignalR();
			const service = getActivityLogSignalR();
			this.signalRUnsubscribe = service.subscribe((event) => {
				const isRelevant =
					(this.quoteId && event.quoteIds.includes(this.quoteId)) ||
					(this.orderId && event.orderIds.includes(this.orderId));
				if (isRelevant) {
					this.fetchNewEntries();
				}
			});
		},

		unsubscribeSignalR() {
			if (this.signalRUnsubscribe) {
				this.signalRUnsubscribe();
				this.signalRUnsubscribe = null;
			}
		},

		async fetchNewEntries() {
			if (this.loading) return;

			try {
				const orFilters = [];
				if (this.quoteId) orFilters.push({ quoteId: { eq: this.quoteId } });
				if (this.orderId) orFilters.push({ orderId: { eq: this.orderId } });
				const where = orFilters.length > 1 ? { or: orFilters } : orFilters[0];

				const res = await this.$apollo.query({
					query: GET_ACTIVITY_LOGS,
					variables: {
						where,
						order: [{ timestamp: "ASC" }],
						skip: this.items.length,
						take: this.pageSize,
					},
					fetchPolicy: "no-cache",
				});

				if (res.data.activityLogs.items.length > 0) {
					this.items = [...this.items, ...res.data.activityLogs.items];
					this.totalCount = res.data.activityLogs.totalCount;

					this.$nextTick(() => {
						const el = this.$refs.timelineScroll;
						if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
					});
				}
			} catch (err) {
				// Silently ignore fetch errors
			}
		},

		isOrderPhase(item) {
			if (ORDER_PHASE_ACTIONS.has(item.requestName)) return true;
			// Entries with orderId belong to order phase — even if they also have quoteId
			// (e.g., payments/notes are stored on the quote but added from the order view)
			if (item.orderId) return true;
			return false;
		},

		groupByTimestamp(items) {
			const groups = [];
			let currentGroup = null;

			items.forEach((item) => {
				// Group items within the same second by the same user
				const key = item.timestamp.substring(0, 19) + "|" + (item.userName || "");
				if (currentGroup && currentGroup.key === key) {
					currentGroup.items.push(item);
				} else {
					currentGroup = { key, items: [item] };
					groups.push(currentGroup);
				}
			});

			return groups;
		},

		relativeTime(timestamp) {
			const now = new Date();
			const then = new Date(timestamp);
			const diffMs = now - then;
			const diffSec = Math.floor(diffMs / 1000);
			const diffMin = Math.floor(diffSec / 60);
			const diffHour = Math.floor(diffMin / 60);
			const diffDay = Math.floor(diffHour / 24);

			if (diffSec < 60) return "just now";
			if (diffMin < 60) return `${diffMin}m ago`;
			if (diffHour < 24) return `${diffHour}h ago`;
			if (diffDay < 7) return `${diffDay}d ago`;
			return "";
		},

		displayText(item) {
			const hasDescriptiveSummary = item.requestSummary && !/^(\w+=[^,]+,?\s*)+$/.test(item.requestSummary);
			let text = hasDescriptiveSummary ? item.requestSummary : this.humanizeRequestName(item.requestName);
			return text.replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>');
		},

		humanizeRequestName(name) {
			const labels = {
				SubmitForApproval: "Submitted for approval",
				ApproveQuote: "Approved quote",
				CancelQuote: "Cancelled quote",
				RestoreQuote: "Restored quote",
				RevertToPending: "Reverted to quote",
				CancelOrder: "Cancelled order",
				RestoreOrder: "Restored order",
				UpdateOrder: "Updated order",
				PrintDocument: "Printed document",
				CommitInventory: "Committed inventory",
				CommitSizedInventory: "Committed sized inventory",
				CreatePurchaseOrder: "Created purchase order",
				UpdatePurchaseOrder: "Updated purchase order",
				CancelPurchaseOrder: "Cancelled purchase order",
				LockPurchaseOrder: "Locked purchase order",
				RestorePurchaseOrder: "Restored purchase order",
				EditPurchaseOrderVendor: "Changed PO vendor",
				EditPurchaseOrderDate: "Changed PO date",
				AddOrUpdatePoNotes: "Updated PO notes",
				ReceiveInventory: "Received inventory",
				MarkSampleReturned: "Marked sample returned",
			};
			if (labels[name]) return labels[name];
			if (name.includes(" ")) return name;
			return name.replace(/([A-Z])/g, " $1").trim();
		},

		actionIcon(name) {
			return ACTION_ICONS[name] || "mdi-information-outline";
		},

		dotColor(outcome, requestName) {
			if (outcome === "Error" || outcome === "Unauthorized") return "#f44336";
			if (outcome === "Failure") return "#ff9800";

			const actionColors = {
				"Created Quote": "#1565c0",
				"Updated Quote": "#1565c0",
				"Added Item": "#2e7d32",
				"Added Sub-Item": "#2e7d32",
				"Assigned Item": "#1565c0",
				"Added Draft Item": "#9e9e9e",
				"Removed Item": "#c62828",
				"Colors Selected": "#7b1fa2",
				"Colors Updated": "#7b1fa2",
				"Sizes Set": "#00838f",
				"Sizes Updated": "#00838f",
				"Special Sizes Set": "#00838f",
				"Special Sizes Updated": "#00838f",
				"Line Item Note": "#5d4037",
				"Line Item Note Updated": "#5d4037",
				"Line Item Note Removed": "#c62828",
				"Updated Notes": "#5d4037",
				"S&H Updated": "#e65100",
				"S&H Discount Updated": "#e65100",
				"Tax Rate Updated": "#e65100",
				"Contact Changed": "#1565c0",
				"Bill To Changed": "#1565c0",
				"Ship To Changed": "#1565c0",
				"Sales Rep Changed": "#1565c0",
				"Shipping Method Changed": "#1565c0",
				"Payment Terms Changed": "#1565c0",
				"Expected Order Date Changed": "#1565c0",
				"School PO Changed": "#1565c0",
				"Reference # Changed": "#1565c0",
				"Attribution Changed": "#1565c0",
				"Updated Payments": "#e65100",
				"Payment Added": "#2e7d32",
				"Payment Updated": "#e65100",
				"Payment Removed": "#c62828",
				"Placed Order": "#1565c0",
				"Processed Order": "#1565c0",
				"Submitted for Approval": "#f9a825",
				"Approved Quote": "#2e7d32",
				"Cancelled Quote": "#c62828",
				"Restored Quote": "#2e7d32",
				"Reverted to Quote": "#f9a825",
				"Quantity Changed": "#e65100",
				"Text Options Set": "#6a1b9a",
				"Text Options Updated": "#6a1b9a",
				"Lettering Set": "#4527a0",
				"Lettering Added": "#4527a0",
				"Lettering Updated": "#4527a0",
				"Lettering Removed": "#c62828",
				"Pricing Set": "#2e7d32",
				"Pricing Changed": "#e65100",
				"Drop Ship": "#00838f",
				"Drop Ship Changed": "#00838f",
				"Braid Position Set": "#5d4037",
				"Braid Position Changed": "#5d4037",
				"Braid Position Removed": "#c62828",
				"Match Previous PO Set": "#1565c0",
				"Match Previous PO Changed": "#1565c0",
				"Match Previous PO Removed": "#c62828",
				"Colors Cleared": "#c62828",
				"Sizes Cleared": "#c62828",
				"Special Sizes Cleared": "#c62828",
				SubmitForApproval: "#f9a825",
				ApproveQuote: "#2e7d32",
				CancelQuote: "#c62828",
				"Cancelled Order": "#c62828",
				"Restored Order": "#2e7d32",
				CancelOrder: "#c62828",
				RestoreQuote: "#2e7d32",
				RestoreOrder: "#2e7d32",
				RevertToPending: "#f9a825",
				"Line Item Status Changed": "#00838f",
				"Inventory Committed": "#2e7d32",
				"Pick Ticket Created": "#1565c0",
				"Pick Ticket Unshipped": "#e65100",
				"Sub-Item Added to Processing Pick Ticket": "#1565c0",
				"Parent Added to Processing Pick Ticket": "#1565c0",
				"Processing Pick Ticket Converted to Normal": "#2e7d32",
				"Sub-Item Removed from Processing Pick Ticket": "#e65100",
				"Line Item Unshipped": "#e65100",
				"Purchase Order Generated": "#4527a0",
				"Dropship PO Generated": "#e65100",
				"Inventory Received": "#2e7d32",
				"Inventory Unreceived": "#e65100",
				"Received Inventory Committed": "#2e7d32",
				"Received Inventory Decommitted": "#c62828",
				"Shipping Label Created": "#1565c0",
				"Shipping Label Voided": "#c62828",
				"Document Printed": "#616161",
				"Auto-Print Failed": "#c62828",
				PrintDocument: "#616161",
				UpdatePurchaseOrder: "#e65100",
				CreatePurchaseOrder: "#4527a0",
				CancelPurchaseOrder: "#c62828",
				RestorePurchaseOrder: "#2e7d32",
				LockPurchaseOrder: "#f9a825",
				EditPurchaseOrderVendor: "#1565c0",
				EditPurchaseOrderDate: "#1565c0",
				AddOrUpdatePoNotes: "#5d4037",
			};

			return actionColors[requestName] || "#4caf50";
		},
	},
};
</script>

<style scoped>
.timeline-scroll {
	max-height: 70vh;
	overflow-y: auto;
}

.entity-label {
	font-size: 1.3rem;
	font-weight: 400;
	color: #616161;
	border-left: 1px solid #e0e0e0;
	padding-left: 0.8rem;
}

.event-count {
	font-size: 1.1rem;
	font-weight: 400;
	color: #9e9e9e;
}

.search-input {
	font-size: 1.3rem;
}

.search-input >>> .v-input__slot {
	min-height: 32px !important;
}

.search-input >>> input {
	font-size: 1.3rem;
}

.phase-section {
	padding: 0 1.6rem;
}

.phase-section:first-child {
	padding-top: 1.2rem;
}

.phase-header {
	font-size: 1.2rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: #616161;
	padding: 1.2rem 0 0.4rem 0;
	display: flex;
	align-items: center;
	border-bottom: 1px solid #e0e0e0;
	margin-bottom: 0.8rem;
}

.phase-header-clickable {
	cursor: pointer;
	user-select: none;
}

.phase-header-clickable:hover {
	color: #424242;
}

.phase-count {
	font-weight: 400;
	font-size: 1.1rem;
	color: #9e9e9e;
}

.activity-timeline {
	padding-top: 0;
}

.activity-timeline >>> .v-timeline-item__dot {
	box-shadow: none;
	height: 32px;
	width: 32px;
}

.activity-timeline >>> .v-timeline-item__dot .v-timeline-item__inner-dot {
	height: 32px;
	width: 32px;
}

.activity-timeline >>> .v-timeline-item__dot .v-icon {
	font-size: 16px !important;
}

.activity-timeline >>> .v-timeline-item__body {
	padding: 0.4rem 0 1.2rem 0;
}

.timeline-entry {
	line-height: 1.5;
}

.timeline-meta {
	font-size: 1.1rem;
	color: #9e9e9e;
	margin-bottom: 0.2rem;
	display: flex;
	align-items: center;
	gap: 0.6rem;
}

.timestamp {
	white-space: nowrap;
}

.relative-time {
	font-size: 1rem;
	color: #bdbdbd;
	font-style: italic;
}

.user-badge {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 22px;
	height: 22px;
	border-radius: 50%;
	color: white;
	font-size: 0.9rem;
	font-weight: 600;
	flex-shrink: 0;
	cursor: default;
}

.timeline-description {
	font-size: 1.3rem;
	color: #212121;
}

.timeline-error {
	font-size: 1.2rem;
	color: #f44336;
	margin-top: 0.2rem;
}
</style>
